import { invariant } from '../invariant'

/**
 * Class for building event names with a specific structure.
 */
export class EventNameBuilder {
  /**
   * The action part of the event name.
   * @private
   * @type {string | null}
   */
  _action = null

  /**
   * The component name part of the event name.
   * @private
   * @type {string | null}
   */
  _componentName = null

  /**
   * The page name part of the event name.
   * @private
   * @type {string | null}
   */
  _pageName = null

  /**
   * The prefix part of the event name.
   * @private
   * @type {string | null}
   */
  _prefix = null

  /**
   * Sets the action part of the event name.
   * @param {string} action - The action to set.
   * @returns {EventNameBuilder} The current instance for method chaining.
   */
  withAction(action) {
    this._action = action
    return this
  }

  /**
   * Sets the component name part of the event name.
   * @param {string} componentName - The component name to set.
   * @returns {EventNameBuilder} The current instance for method chaining.
   */
  withComponentName(componentName) {
    this._componentName = componentName
    return this
  }

  /**
   * Sets the page name part of the event name.
   * @param {string} pageName - The page name to set.
   * @returns {EventNameBuilder} The current instance for method chaining.
   */
  withPageName(pageName) {
    this._pageName = pageName
    return this
  }

  /**
   * Sets the prefix part of the event name.
   * @param {string} prefix - The prefix to set.
   * @returns {EventNameBuilder} The current instance for method chaining.
   */
  withPrefix(prefix) {
    this._prefix = prefix
    return this
  }

  /**
   * Builds and returns the complete event name.
   * @returns {string} The constructed event name.
   * @throws {Error} If any of the required parts (action, componentName, pageName, prefix) are missing.
   */
  build() {
    invariant(
      this._action && this._pageName && this._prefix,
      'All parts of the event name (action, pageName, prefix) must be set before building.',
    )

    if (this._componentName) {
      return `${this._prefix}_${this._pageName}_${this._componentName}_${this._action}`
    }

    return `${this._prefix}_${this._pageName}_${this._action}`
  }
}
